/*
    Copyright 2020 Lauren Gyldenlove.
    This file is part of Duelmaster.

    Duelmaster is free software: you can redistribute it and/or modify
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Duelmaster is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with Foobar.  If not, see <https://www.gnu.org/licenses/>.
 */

import React, { useState } from 'react';
import { AppContext } from '../App';
import * as SettingsUtil from '../model/AppSettings';
import * as Player from '../model/Player';
import SettingsIcon from '@material-ui/icons/Settings';
import VolumeDown from '@material-ui/icons/VolumeDown';
import VolumeUp from '@material-ui/icons/VolumeUp';
import Dialog from '@material-ui/core/Dialog';
import { Grid, Button, TextField, DialogContent, DialogActions, DialogContentText, Slider, Typography, ListItem, ListItemIcon, ListItemText} from '@material-ui/core';
import { ToolItemAppBar } from './ToolItemAppBar';

export const SettingsToolItem = () => {
    const [open, setOpen] = useState(false);
    const handleOpen = () => {
        setOpen(true);
    }
    const handleClose = () => {
        setOpen(false);
    }

    return (
        <React.Fragment>
            <ListItem button onClick={handleOpen}>
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText primary={"Settings"} />
            </ListItem>
            <Dialog fullScreen aria-labelledby="settings-title" aria-describedby="settings-description" open={open} onClose={handleClose}>
                <ToolItemAppBar handleClose={handleClose} title={"Settings"} />
                <DialogContent>
                    <DialogContentText id="settings-description">
                        Settings are automatically saved as you type.
                    </DialogContentText>
                    <SettingsView />
                </DialogContent>
                <DialogActions>
                        <Button color="primary" onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

const SettingsView = () => {
    const context = React.useContext<SettingsUtil.AppSettings>(AppContext);

    React.useEffect(() => {
        console.log("Saving settings to local storage.");
        SettingsUtil.saveToLocalStorage(context);
    }, [context]);

    function updateInitialLifePoints(event: React.ChangeEvent<HTMLInputElement>) {
        const initialLifePoints = event.currentTarget.valueAsNumber;
        context.setInitialLifePoints(initialLifePoints);
    }

    function updateMatches(event: React.ChangeEvent<HTMLInputElement>) {
        const newValue = event.currentTarget.valueAsNumber;
        if(newValue > 0 && newValue < 11) {
            context.setMatches(newValue);
        }
    }

    function updateLeftPlayerName(event: React.ChangeEvent<HTMLInputElement>) {
        const leftPlayer = Player.create(event.currentTarget.value, Player.Position.LEFT);
        context.setLeftPlayer(leftPlayer);
    }

    function updateRightPlayerName(event: React.ChangeEvent<HTMLInputElement>) {
        const rightPlayer = Player.create(event.currentTarget.value, Player.Position.RIGHT);
        context.setRightPlayer(rightPlayer);
    }

    function updateVolume(event: any, newValue: number | number[]) {
        context.setVolume(newValue as number);
    }

    return (
        <div className="settings">
            <Typography variant="h6" id="game-settings">Game Settings</Typography>
            <TextField
                type="number"
                fullWidth
                id="initialLifePoints"
                label="Initial Life Points"
                defaultValue={context.initialLifePoints}
                onChange={updateInitialLifePoints}
                margin="normal"
            />
            <TextField
                type="number"
                fullWidth
                id="matches"
                label="Number of matches"
                defaultValue={context.matches}
                onChange={updateMatches}
                margin="normal"
            />


            <Typography variant="h6" id="player-settings">Player Settings</Typography>
            <Typography id="player-names">
                Names
            </Typography>
            <TextField 
                fullWidth 
                id="leftPlayerName" 
                label="Left Player Name" 
                defaultValue={context.leftPlayer.name} 
                onChange={updateLeftPlayerName}
                margin="normal"
            />

            <TextField 
                fullWidth 
                id="rightPlayerName" 
                label="Right Player Name" 
                defaultValue={context.rightPlayer.name} 
                onChange={updateRightPlayerName} 
                margin="normal"
            />

            <Typography variant="h6">Application Settings</Typography>
            <Typography id="volume-label">
                Volume
            </Typography>
            <Grid container spacing={2}>
                <Grid item>
                    <VolumeDown />
                </Grid>
                <Grid item xs>
                    <Slider defaultValue={context.volume} min={0} max={100} onChange={updateVolume} />
                </Grid>
                <Grid item>
                    <VolumeUp />
                </Grid>
            </Grid>
            
        </div>
    );
}